import { jsx as _jsx } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2026-03-04 19:26:04 +0100 (Wed, 04 Mar 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 161228 $
//  Purpose   : splitter helpers
// =============================================================================
// ---Third-party imports
import React from 'react';
import { ReflexContainer, ReflexSplitter, ReflexElement } from 'uix/chunks';
import clsx from 'clsx';
// ---Framework imports
import { usePersistentShallow, useEffect, useRef } from 'uix/hooks';
import { sum, isNil, isString, isArray, isNumber, range } from 'ldash';
// ---Local imports
import S from './splitpane.scss';
// ---Exports
export const SplitPane = ({ name = 'SplitPane', style, children, flexInner, flex, vertical = false, windowResizeAware = false, persist, sizes, className, classNames, styles, overflow, borderless })=>{
    /* simplified ReflexContainer
  Usage:
    <SplitPane>
      <div>pane1</div>
      <div>pane2</div>
    </SplitPane>
  Usage with Fragments (fragment elements change flexDirection):
    <SplitPane vertical>
      <>
        <Header>Left</Header>
        <div>pane1</div>
      </>
      <>
        <Header>Right</Header>
        <div>pane2</div>
      </>
    </SplitPane>
  */ if (isString(persist)) {
        name = persist;
        persist = true;
    }
    const normalizeFlexes = (vals, count)=>{
        if (count < 1) return [];
        if (!isArray(vals)) {
            return Array(count).fill(1.0 / count);
        }
        let next = range(count).map((i)=>isNumber(vals[i]) && vals[i] >= 0 ? vals[i] : 0);
        let total = sum(next);
        if (!(total > 0)) {
            return Array(count).fill(1.0 / count);
        }
        if (Math.abs(total - 1) < 0.001) {
            return next;
        }
        return next.map((v)=>v / total);
    };
    let [opts, setOpts] = usePersistentShallow(name, {}, 13);
    let nonEmpty = [];
    React.Children.forEach(children, (e, i)=>{
        if (!isNil(e) && e !== '') nonEmpty.push(e);
    });
    let num_children = nonEmpty.length;
    let stored_flexes = opts[num_children];
    let flexes = persist && stored_flexes ? stored_flexes : sizes;
    flexes = normalizeFlexes(flexes, num_children);
    let flexesRef = useRef(flexes);
    useEffect(()=>{
        flexesRef.current = flexes;
    }, [
        num_children,
        name,
        vertical,
        flexes
    ]);
    let orientation = vertical ? 'vertical' : 'horizontal';
    let innerStyle = flexInner ? {
        display: 'flex',
        overflow: 'hidden'
    } : {};
    let outerStyle = flex ? {
        ...style,
        flex: '1'
    } : style;
    if (overflow) {
        innerStyle = {
            ...innerStyle,
            overflow
        };
        outerStyle = {
            ...outerStyle,
            overflow
        };
    }
    let getBaseFlexes = ()=>isArray(flexesRef.current) && flexesRef.current.length == num_children ? [
            ...flexesRef.current
        ] : normalizeFlexes(null, num_children);
    let persistFlex = (index, value)=>{
        if (persist && num_children > 1) {
            let base = getBaseFlexes();
            if (isNumber(value) && value >= 0) {
                base[index] = value;
            }
            flexesRef.current = base;
        }
    };
    let persistFlexStop = (index, event)=>{
        if (persist && num_children > 1) {
            let base = getBaseFlexes();
            let stopFlex = event.component.props.flex;
            if (isNumber(stopFlex) && stopFlex >= 0) {
                base[index] = stopFlex;
            }
            let values = normalizeFlexes(base, num_children);
            flexesRef.current = values;
            setOpts({
                [num_children]: values
            });
        }
    };
    let els = [];
    nonEmpty.forEach((e, i)=>{
        let istyle = {
            flexDirection: 'column',
            ...innerStyle,
            ...styles?.[i] || {}
        };
        els.push(/*#__PURE__*/ _jsx(ReflexElement, {
            flex: num_children > 1 ? flexes[i] : 1,
            className: classNames?.[i],
            style: istyle,
            onResize: persist ? (event)=>persistFlex(i, event.component.props.flex) : undefined,
            onStopResize: persist ? (event)=>persistFlexStop(i, event) : undefined,
            children: e
        }, `re_${i}`));
        if (i < nonEmpty.length - 1) {
            els.push(/*#__PURE__*/ _jsx(ReflexSplitter, {
                className: vertical ? S.splitterV : S.splitterH
            }, `rs_${i}`));
        }
    });
    if (process.env.UIX_ENV === 'express') {
        // ReflexContainer seems not to work - so fake one
        return /*#__PURE__*/ _jsx("div", {
            style: outerStyle,
            className: clsx(!borderless && S.bordered, S.reflexContainer, S.ssrReflexContainer, vertical ? S.ssrVertical : S.ssrHorizontal),
            children: els
        });
    }
    return /*#__PURE__*/ _jsx(ReflexContainer, {
        windowResizeAware: windowResizeAware,
        style: outerStyle,
        orientation: orientation,
        className: clsx(!borderless && S.bordered, S.reflexContainer, className),
        children: els
    });
};
